import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import type {
  BaseCurrencyAmountConfig,
  CurrencyAmountConfig,
  CurrencyAmountFormFieldConfig,
} from '@/shared/types/currencyAmountFormTypes.ts';

export interface CurrencyAmountFormValues extends Record<string, unknown> {
  currencyCode: string;
  amount: string;
}

export interface CurrencyAmountFormOptions extends BaseCurrencyAmountConfig {
  currencyOptions: Array<{
    value: string;
    label: string;
    name: string;
    icon: string;
    min: number;
  }>;
  minAmount?: number;
  currencyLabelKey?: string;
  amountLabelKey?: string;
  amountPlaceholderKey?: string;
  submitButtonKey?: string;
  cancelButtonKey?: string;
  customContentBeforeSubmit?: React.ReactNode;
  customContentAfterFields?: React.ReactNode;
}

export const createCurrencyAmountFormSchema = (
  t: TFunction,
  minAmount?: number,
  currencyCode?: string
) => {
  const required = t('common.required', 'This field is required');
  const minAmountMessage = minAmount
    ? t('common.minAmount', {
        amount: minAmount,
        currency: currencyCode || '',
      })
    : required;

  return z.object({
    currencyCode: z.string().nonempty({ message: required }),
    amount: z
      .string({ error: required })
      .refine(val => (minAmount ? Number(val) >= minAmount : true), {
        message: minAmountMessage,
      }),
  });
};

export const createCurrencyAmountFormConfig = (
  t: TFunction,
  options: CurrencyAmountFormOptions
): CurrencyAmountConfig<CurrencyAmountFormValues> => {
  const {
    currencyOptions,
    minAmount,
    currencyLabelKey = 'common.currency',
    amountLabelKey = 'common.amount',
    amountPlaceholderKey,
    submitButtonKey = 'common.submit',
    cancelButtonKey = 'common.cancel',
    showCancelButton = false,
    customContentBeforeSubmit,
    customContentAfterFields,
    className,
  } = options;

  const fields: CurrencyAmountFormFieldConfig<CurrencyAmountFormValues>[] = [
    {
      name: 'currencyCode',
      type: 'select',
      label: t(currencyLabelKey),
      options: currencyOptions,
      required: true,
      validation: {
        required: t('common.required'),
      },
    },
    {
      name: 'amount',
      type: 'input',
      label: t(amountLabelKey),
      placeholder: amountPlaceholderKey ? t(amountPlaceholderKey) : '',
      required: true,
      validation: {
        required: t('common.required'),
        min: minAmount
          ? {
              value: minAmount,
              message: t('common.minAmount', {
                amount: minAmount,
                currency: '',
              }),
            }
          : undefined,
      },
    },
  ];

  return {
    fields,
    validationSchema: createCurrencyAmountFormSchema(t, minAmount),
    defaultValues: {
      currencyCode: '',
      amount: '',
    },
    submitButtonText: t(submitButtonKey),
    submitButtonClassName: options.submitButtonClassName,
    submitButtonIcon: options.submitButtonIcon,
    cancelButtonText: t(cancelButtonKey),
    showCancelButton,
    customContentBeforeSubmit,
    customContentAfterFields,
    className,
  };
};

export const useCurrencyAmountFormConfig = (
  options: CurrencyAmountFormOptions
) => {
  const { t } = useTranslation();

  return useMemo(
    () => createCurrencyAmountFormConfig(t, options),
    [
      t,
      options.currencyOptions,
      options.minAmount,
      options.currencyLabelKey,
      options.amountLabelKey,
      options.amountPlaceholderKey,
      options.submitButtonKey,
      options.cancelButtonKey,
      options.showCancelButton,
      options.customContentBeforeSubmit,
      options.customContentAfterFields,
      options.className,
    ]
  );
};
